import {
	Button,
	SearchSelect,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import BusinessLogicContext, {
	BusinessLogicContextProps,
} from "contexts/BusinessLogicContext";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { logicType } from "schemas/elements";
import TwinInput, { fields } from "./TwinInput";
import api from "lib/api";

interface Props {
	index: number;
	item: logicType;
	nameOptions: { label: string; options: SearchSelectOptionProps[] }[];
	loading: boolean;
}

export default ({ item, index, nameOptions, loading }: Props) => {
	const { t } = useTranslation();

	const { action, handleUpdateStore, elementIndex, validation, value } =
		useContext(BusinessLogicContext) as BusinessLogicContextProps;

	const [name, setName] = useState<SearchSelectOptionProps | undefined>(
		item.name
			? {
					value: `${item.name}/${item.shared}`,
					label: item.name,
			  }
			: undefined
	);

	const [paramLabels, setParamLabels] = useState<string[]>([]);
	const [retrieveError, setRetrieveError] = useState(false);

	const handleNameChange = useCallback(
		async newVal => {
			setName(newVal);
			const currentValues: logicType[] = [...value?.logic];
			let params: Pick<logicType, "params"> = { params: {} };
			// GET business logic and set parameter labels
			if (newVal !== undefined && newVal !== null) {
				try {
					const newParams = await api.businessLogic.getLogic(
						newVal.label,
						/true$/.test(newVal?.value) ? true : false
					);
					params = {
						params: newParams,
					};
					const newLabels: string[] = [];
					for (const [key, value] of Object.entries(newParams)) {
						newLabels.push(value);
						newParams[key] = "";
					}
					if (retrieveError) setRetrieveError(false);
					setParamLabels(newLabels);
				} catch {
					if (!retrieveError) setRetrieveError(true);
				}
			} else {
				setParamLabels([]);
			}
			currentValues[index] = {
				...value.logic[index],
				name: newVal?.label,
				shared: /true$/.test(newVal?.value) ? true : false,
				...params,
			};
			handleUpdateStore({
				...value,
				logic: [...currentValues],
			});
		},
		[handleUpdateStore, index, retrieveError, value]
	);

	const handleRemove = useCallback(() => {
		const currentValues = [...value.logic];
		currentValues.splice(index, 1);
		handleUpdateStore({
			...value,
			logic: [...currentValues],
		});
	}, [handleUpdateStore, index, value]);

	// Reformat param values so they are correct when passed into the store
	const reformatParamValues = useCallback(
		(vals: fields[]) => {
			const currentValues = [...value.logic];
			currentValues[index] = {
				...value.logic[index],
				params: Object.assign(
					{},
					...vals.map((item: fields) => {
						return {
							[item.fieldOne !== undefined
								? item.fieldOne.trim()
								: ""]:
								item.fieldTwo !== undefined
									? item.fieldTwo.trim()
									: "",
						};
					})
				),
			};
			return currentValues;
		},
		[index, value.logic]
	);

	const initLabels = useCallback(async () => {
		// GET business logic and set parameter labels
		if (name !== undefined && name !== null) {
			try {
				const newParams = await api.businessLogic.getLogic(
					name.label,
					/true$/.test(name?.value) ? true : false
				);
				const newLabels: string[] = [];
				for (const [key, value] of Object.entries(newParams)) {
					newLabels.push(value);
					newParams[key] = "";
				}
				if (retrieveError) setRetrieveError(false);
				setParamLabels(newLabels);
			} catch {
				if (!retrieveError) setRetrieveError(true);
			}
		}
	}, [name, retrieveError]);

	useEffect(() => {
		initLabels();
	}, [initLabels]);

	return (
		<div
			className={`elem-logic-properties`}
			key={`${action}-${elementIndex}-logicContainer-${index}`}>
			<div className="elem-logic-delete">
				<Button
					label={t("common.labels.delete")}
					onClick={handleRemove}
					className="btn-outline-danger"
				/>
			</div>
			<div className="elem-logic-wrapper">
				<SearchSelect
					placeholder={t("formDesigner.placeholders.select")}
					value={name}
					options={nameOptions}
					label={t("formDesigner.labels.name")}
					onChange={handleNameChange}
					className={validation && !item.name ? " invalid" : ""}
					clearable={true}
					loading={loading}
					disabled={!nameOptions}
					hint={t(`formDesigner.elements.businessLogic.logicName`)}
					status={
						retrieveError && name !== undefined && name !== null
							? "error"
							: undefined
					}
					feedback={
						retrieveError && name !== undefined && name !== null
							? t("alerts.errorRetrievingLogic", {
									name:
										name !== undefined && name !== null
											? `"${name.label}"`
											: "",
									shared: /true$/.test(name?.value)
										? "shared"
										: "",
							  })
							: undefined
					}
				/>
				{paramLabels.length > 0 && (
					<TwinInput
						paramLabels={paramLabels}
						nextProp={"logic"}
						reformatValues={reformatParamValues}
						values={item.params}
						logicIndex={index}
						hint={t(
							`formDesigner.elements.businessLogic.logicParams`
						)}
					/>
				)}
			</div>
		</div>
	);
};
